import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Search, Building2, CreditCard, ArrowLeft, CalendarDays, CheckCircle2, Users,
    DollarSign, Banknote, CreditCard as CreditCardIcon, Loader2, AlertTriangle, ArrowRightLeft, Ban
} from 'lucide-react';
import api from '@/lib/api';
import MotivationalBanner from '@/components/MotivationalBanner';

export default function DniLookup() {
    const [searchParams] = useSearchParams();
    const status = searchParams.get('status');
    const ref = searchParams.get('ref');

    const [dni, setDni] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searched, setSearched] = useState(false);
    const [classes, setClasses] = useState([]);
    const [classesLoading, setClassesLoading] = useState(false);
    const [registeringId, setRegisteringId] = useState(null);
    const [unregisteringId, setUnregisteringId] = useState(null);
    const [classMsg, setClassMsg] = useState({ type: '', text: '' });

    // Payment state
    const [gymPlans, setGymPlans] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [plansLoading, setPlansLoading] = useState(false);
    const [creatingPayment, setCreatingPayment] = useState(false);
    const [paymentMsg, setPaymentMsg] = useState('');
    const [phrases, setPhrases] = useState([]);
    const [phrasesRandom, setPhrasesRandom] = useState(true);
    const [creatingTransferPromise, setCreatingTransferPromise] = useState(false);
    const [transferMsg, setTransferMsg] = useState('');

    useEffect(() => {
        if (status && ref) {
            const msg = status === 'success'
                ? 'Pago aprobado. Tu tarjeta será activada en breve.'
                : status === 'pending'
                ? 'Pago pendiente de confirmación.'
                : 'El pago no pudo completarse. Intentá de nuevo.';
            setPaymentMsg(msg);
        }
    }, [status, ref]);

    const fetchClasses = async (dniVal) => {
        setClassesLoading(true);
        setClassMsg({ type: '', text: '' });
        try {
            const res = await api.get(`/public/upcoming-classes/${dniVal}`);
            setClasses(res.data.data);
        } catch {
            setClasses([]);
        } finally {
            setClassesLoading(false);
        }
    };

    const refreshCardData = async (dniVal) => {
        try {
            const res = await api.get(`/public/card-by-dni/${dniVal}`);
            setResult(res.data.data);
        } catch {
            /* ignore card refresh errors */ }
    };

    const fetchPaymentData = async (gymId) => {
        setPlansLoading(true);
        try {
            const [plansRes, methodsRes] = await Promise.all([
                api.get(`/public/gym/${gymId}/plans`),
                api.get(`/public/gym/${gymId}/payment-methods`),
            ]);
            setGymPlans(plansRes.data.data || []);
            setPaymentMethods(methodsRes.data.data || []);
        } catch {
            setGymPlans([]);
            setPaymentMethods([]);
        } finally {
            setPlansLoading(false);
        }
    };

    const handleRegister = async (classId) => {
        setRegisteringId(classId);
        setClassMsg({ type: '', text: '' });
        try {
            const res = await api.post('/public/register-to-class', { dni: dni.trim(), class_id: classId });
            setClassMsg({ type: 'success', text: res.data.message });

            const tarjeta = res.data?.data;
            if (tarjeta) {
                setResult(prev => {
                    if (!prev?.tarjeta) return prev;
                    return {
                        ...prev,
                        tarjeta: {
                            ...prev.tarjeta,
                            clases_usadas: tarjeta.clases_usadas,
                            clases_totales: tarjeta.clases_totales,
                            clases_restantes: tarjeta.clases_restantes,
                        },
                    };
                });
            }

            setClasses(prev => prev.map(cls =>
                cls.id === classId
                    ? {
                        ...cls,
                        inscripto: true,
                        puede_desinscribirse: true,
                        lugares_disponibles: cls.lugares_disponibles !== null
                            ? Math.max(0, cls.lugares_disponibles - 1)
                            : null,
                    }
                    : cls
            ));
        } catch (err) {
            setClassMsg({ type: 'error', text: err.response?.data?.message || 'Error al inscribirse.' });
        } finally {
            setRegisteringId(null);
        }
    };

    const handleUnregister = async (classId) => {
        setUnregisteringId(classId);
        setClassMsg({ type: '', text: '' });
        try {
            const res = await api.post('/public/unregister-from-class', { dni: dni.trim(), class_id: classId });
            setClassMsg({ type: 'success', text: res.data.message });

            const tarjeta = res.data?.data;
            if (tarjeta) {
                setResult(prev => {
                    if (!prev?.tarjeta) return prev;
                    return {
                        ...prev,
                        tarjeta: {
                            ...prev.tarjeta,
                            clases_usadas: tarjeta.clases_usadas,
                            clases_totales: tarjeta.clases_totales,
                            clases_restantes: tarjeta.clases_restantes,
                        },
                    };
                });
            }

            setClasses(prev => prev.map(cls =>
                cls.id === classId
                    ? {
                        ...cls,
                        inscripto: false,
                        puede_desinscribirse: false,
                        lugares_disponibles: cls.lugares_disponibles !== null
                            ? cls.lugares_disponibles + 1
                            : null,
                    }
                    : cls
            ));
        } catch (err) {
            setClassMsg({ type: 'error', text: err.response?.data?.message || 'Error al desinscribirse.' });
        } finally {
            setUnregisteringId(null);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!dni.trim()) return;
        setLoading(true);
        setError('');
        setResult(null);
        setSearched(true);
        setPaymentMsg('');
        setTransferMsg('');
        setGymPlans([]);
        setPaymentMethods([]);
        try {
            const res = await api.get(`/public/card-by-dni/${dni.trim()}`);
            const data = res.data.data;
            setResult(data);
            fetchClasses(dni.trim());

            // Fetch motivational phrases for this gym
            if (data.gimnasio?.id) {
                try {
                    const phrasesRes = await api.get(`/public/gym/${data.gimnasio.id}/motivational-phrases`);
                    setPhrases(phrasesRes.data.data || []);
                    setPhrasesRandom(phrasesRes.data.random ?? true);
                } catch {}
            }
        } catch (err) {
            setError(err.response?.data?.message || 'No se encontró un alumno con ese DNI.');
            setClasses([]);
        } finally {
            setLoading(false);
        }
    };

    const [cashMsg, setCashMsg] = useState('');
    const [creatingCashPromise, setCreatingCashPromise] = useState(false);

    const handleTransferPayment = async (planId) => {
        setCreatingTransferPromise(true);
        setTransferMsg('');
        try {
            const res = await api.post('/public/create-transfer-promise', {
                dni: dni.trim(),
                gym_plan_id: planId,
            });
            setTransferMsg(res.data.data?.message || 'Tu tarjeta fue renovada. Realizá la transferencia y el gimnasio la confirmará.');
            refreshCardData(dni.trim());
            fetchClasses(dni.trim());
        } catch (err) {
            setPaymentMsg(err.response?.data?.message || 'Error al crear la promesa de transferencia.');
        } finally {
            setCreatingTransferPromise(false);
        }
    };

    const handlePayWithMp = async (planId) => {
        setCreatingPayment(true);
        setCashMsg('');
        try {
            const res = await api.post('/public/create-payment-preference', {
                dni: dni.trim(),
                gym_plan_id: planId,
            });
            const initPoint = res.data.data?.init_point;
            if (initPoint) {
                window.location.href = initPoint;
            }
        } catch (err) {
            setPaymentMsg(err.response?.data?.message || 'Error al crear el pago.');
        } finally {
            setCreatingPayment(false);
        }
    };

    const handleCashPayment = async (planId) => {
        setCreatingCashPromise(true);
        setCashMsg('');
        try {
            const res = await api.post('/public/create-cash-promise', {
                dni: dni.trim(),
                gym_plan_id: planId,
            });
            setCashMsg(res.data.data?.message || 'Tu tarjeta fue renovada. Acercate al gimnasio para abonar en efectivo.');
            refreshCardData(dni.trim());
            fetchClasses(dni.trim());
        } catch (err) {
            setPaymentMsg(err.response?.data?.message || 'Error al crear la promesa de pago.');
        } finally {
            setCreatingCashPromise(false);
        }
    };

    const hasNoActiveCard = result && (!result.tarjeta || !result.tarjeta.activa || (result.tarjeta.clases_restantes !== null && result.tarjeta.clases_restantes <= 0));

    // Fetch gym plans when the customer has no active card or exhausted classes
    useEffect(() => {
        if (hasNoActiveCard && result?.gimnasio?.id) {
            fetchPaymentData(result.gimnasio.id);
        }
    }, [hasNoActiveCard, result?.gimnasio?.id]);

    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(price);
    };

    return (
        <div className="min-h-screen bg-black">
            {/* Navbar */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-black/20">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-white">
                            <path d="M12 2l8.66 5v10l-8.66 5-8.66-5V7z" />
                            <path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
                        </svg>
                        <span className="text-sm font-bold uppercase tracking-[0.2em]">GYMS.ar</span>
                    </Link>
                    <div className="flex items-center gap-2 sm:gap-5">
                        <Link to="/login" className="hidden sm:flex items-center text-white/90 hover:text-white transition-colors uppercase text-xs tracking-[0.15em] font-medium">
                            Ingresar
                        </Link>
                        <Link to="/register">
                            <Button variant="outline" size="sm" className="uppercase tracking-[0.15em] text-xs border-white/60 hover:border-white hover:bg-white hover:text-black">
                                Registrarse
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            <div className="max-w-lg mx-auto px-4 pt-24 pb-12">
                <Link to="/" className="inline-flex items-center text-sm text-white/60 hover:text-white mb-6 underline">
                    <ArrowLeft className="h-4 w-4 mr-1" />Volver al inicio
                </Link>

                <div className="text-center mb-8">
                    <div className="h-14 w-14 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Search className="h-7 w-7 text-white" />
                    </div>
                    <h1 className="text-2xl font-bold uppercase tracking-wide mb-2 text-white">Consultá tu Tarjeta</h1>
                    <p className="text-white/60">Ingresá tu DNI para ver el estado de tu membresía.</p>
                </div>

                <MotivationalBanner phrases={phrases} random={phrasesRandom} intervalSeconds={30} className="mb-6" />

                <form onSubmit={handleSearch} className="flex gap-2 mb-8">
                    <Input
                        placeholder="Ingresá tu DNI..."
                        value={dni}
                        onChange={(e) => setDni(e.target.value)}
                        className="text-lg h-12 bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                        required
                    />
                    <Button type="submit" size="lg" disabled={loading}>
                        {loading ? 'Buscando...' : 'Buscar'}
                    </Button>
                </form>

                {paymentMsg && (
                    <div className={`text-sm p-4 rounded-md mb-6 border ${status === 'success' ? 'border-green-800 bg-green-950/30 text-green-300' : status === 'pending' ? 'border-yellow-800 bg-yellow-950/30 text-yellow-300' : 'border-red-800 bg-red-950/30 text-red-300'}`}>
                        {paymentMsg}
                    </div>
                )}

                {error && searched && (
                    <Card className="bg-[#0a0a0a] border-hairline-dark">
                        <CardContent className="py-8 text-center">
                            <CreditCard className="h-10 w-10 text-white/60 mx-auto mb-3" />
                            <p className="text-white/60">{error}</p>
                        </CardContent>
                    </Card>
                )}

                {result && (
                    <>
                    <Card className="overflow-hidden bg-[#0a0a0a] border-hairline-dark">
                        {/* Card header with gym branding */}
                        <div
                            className="p-6 relative overflow-hidden"
                            style={{
                                backgroundColor: result.gimnasio.card_bg_color || undefined,
                                color: result.gimnasio.card_text_color || undefined,
                                backgroundImage: result.gimnasio.card_banner ? `url(${result.gimnasio.card_banner})` : 'none',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                ...(!result.gimnasio.card_bg_color && !result.gimnasio.card_banner ? { background: '#0a0a0a', color: '#ffffff' } : {}),
                            }}
                        >
                            {result.gimnasio.card_banner && <div className="absolute inset-0 bg-black/50" />}
                            <div className="relative z-10">
                                <div className="flex items-center gap-3 mb-3">
                                    {result.gimnasio.logo ? (
                                        <img src={result.gimnasio.logo} alt="Logo" className="h-10 w-10 object-contain rounded bg-white/10 p-1" />
                                    ) : (
                                        <div className="h-10 w-10 bg-white/10 rounded flex items-center justify-center">
                                            <Building2 className="h-5 w-5 text-white" />
                                        </div>
                                    )}
                                    <span className="font-semibold text-lg text-white">{result.gimnasio.nombre}</span>
                                </div>
                                <p className="text-2xl font-bold text-white">{result.nombre}</p>
                            </div>
                        </div>

                        <CardContent className="p-6">
                            {result.tarjeta ? (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-white">Estado de la Tarjeta</span>
                                        <Badge variant={result.tarjeta.activa ? 'success' : 'outline'}>
                                            {result.tarjeta.activa ? 'ACTIVA' : 'INACTIVA'}
                                        </Badge>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-xs text-white/60 uppercase">Inicio</p>
                                            <p className="font-semibold text-white">{result.tarjeta.fecha_inicio}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-white/60 uppercase">Vencimiento</p>
                                            <p className="font-semibold text-white">{result.tarjeta.fecha_fin}</p>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex justify-between text-sm mb-2">
                                            <span className="text-white/60">Clases</span>
                                            <span className="font-semibold text-white">{result.tarjeta.clases_usadas} / {result.tarjeta.clases_totales}</span>
                                        </div>
                                        <div className="w-full bg-white/10 rounded-full h-3">
                                            <div
                                                className="bg-white rounded-full h-3 transition-all"
                                                style={{ width: `${Math.min((result.tarjeta.clases_usadas / result.tarjeta.clases_totales) * 100, 100)}%` }}
                                            />
                                        </div>
                                        <p className="text-sm text-white/60 mt-1">
                                            {result.tarjeta.clases_restantes} {result.tarjeta.clases_restantes === 1 ? 'clase restante' : 'clases restantes'}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <CreditCard className="h-8 w-8 text-white/60 mx-auto mb-2" />
                                    <p className="text-white/60">No tiene una tarjeta activa en este momento.</p>
                                    {result.ultima_tarjeta && (
                                        <p className="text-sm text-white/60 mt-2">
                                            Última tarjeta vencida: <span className="font-medium text-white">{result.ultima_tarjeta.fecha_fin}</span>
                                        </p>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Payment section when no active card */}
                    {hasNoActiveCard && (
                        <Card className="mt-6 bg-[#0a0a0a] border-hairline-dark">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-lg text-white uppercase tracking-wide">
                                    <DollarSign className="h-5 w-5 text-white" />
                                    Renová tu Membresía
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                            {plansLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-white" />
                                </div>
                            ) : gymPlans.length === 0 ? (
                                <div className="text-center py-6 text-white/40">
                                    <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                                    <p className="text-sm">Este gimnasio aún no tiene planes de suscripción disponibles.</p>
                                    <p className="text-xs mt-1">Contactá al gimnasio para más información.</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {cashMsg && (
                                        <div className="text-sm p-4 rounded-md border border-green-800 bg-green-950/30 text-green-300 mb-4">
                                            {cashMsg}
                                        </div>
                                    )}
                                    {transferMsg && (
                                        <div className="text-sm p-4 rounded-md border border-blue-800 bg-blue-950/30 text-blue-300 mb-4">
                                            {transferMsg}
                                        </div>
                                    )}
                                    {gymPlans.map(plan => (
                                        <div key={plan.id} className="p-4 rounded-lg border border-hairline-dark hover:bg-white/5 transition-colors">
                                            <div className="flex items-start justify-between mb-2">
                                                <div>
                                                    <h4 className="font-bold text-white">{plan.name}</h4>
                                                    <p className="text-xs text-white/60">{plan.description || `${plan.duration_months} mes(es)`}</p>
                                                </div>
                                                <span className="text-lg font-bold text-white">{formatPrice(plan.price)}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-white/50 mb-3">
                                                <span>{plan.duration_months} {plan.duration_months === 1 ? 'mes' : 'meses'}</span>
                                                <span className="text-white/20">|</span>
                                                <span>{plan.total_classes > 0 ? `${plan.total_classes} clases` : 'Clases ilimitadas'}</span>
                                            </div>
                                            <div className="flex gap-2 flex-wrap">
                                                {paymentMethods.find(m => m.id === 'mercadopago') && (
                                                    <Button
                                                        size="sm"
                                                        className="flex-1 uppercase tracking-widest text-[10px]"
                                                        disabled={creatingPayment}
                                                        onClick={() => handlePayWithMp(plan.id)}
                                                    >
                                                        <CreditCardIcon className="h-3.5 w-3.5 mr-1.5" />
                                                        {creatingPayment ? 'Procesando...' : 'Pagar con MercadoPago'}
                                                    </Button>
                                                )}
                                                {paymentMethods.find(m => m.id === 'cash') && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="flex-1 uppercase tracking-widest text-[10px] border-white/30 text-white hover:bg-white/10"
                                                        disabled={creatingCashPromise}
                                                        onClick={() => handleCashPayment(plan.id)}
                                                    >
                                                        <Banknote className="h-3.5 w-3.5 mr-1.5" />
                                                        {creatingCashPromise ? 'Procesando...' : 'Pagar en Efectivo'}
                                                    </Button>
                                                )}
                                                {paymentMethods.find(m => m.id === 'transfer') && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="flex-1 uppercase tracking-widest text-[10px] border-white/30 text-white hover:bg-white/10"
                                                        disabled={creatingTransferPromise}
                                                        onClick={() => handleTransferPayment(plan.id)}
                                                    >
                                                        <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" />
                                                        {creatingTransferPromise ? 'Procesando...' : 'Transferencia'}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Upcoming classes */}
                    <Card className="mt-6 bg-[#0a0a0a] border-hairline-dark">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-lg text-white uppercase tracking-wide">
                                <CalendarDays className="h-5 w-5 text-white" />
                                Próximas Clases
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {classMsg.text && (
                                <div className="text-sm p-3 rounded-md mb-4 border border-white/20 bg-[#0a0a0a] text-white">
                                    {classMsg.text}
                                </div>
                            )}
                            {classesLoading ? (
                                <p className="text-white/60 text-center py-4">Cargando clases...</p>
                            ) : classes.length === 0 ? (
                                <p className="text-white/60 text-center py-4">No hay clases próximas disponibles.</p>
                            ) : (
                                <div className="space-y-3">
                                    {classes.slice(0, 10).map(cls => (
                                        <div key={cls.id} className={`flex items-center justify-between p-3 rounded-lg border border-hairline-dark hover:bg-white/5 ${cls.cancelada ? 'opacity-70' : ''}`}>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <p className="font-medium text-white truncate">{cls.nombre}</p>
                                                    {cls.cancelada && (
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-red-500/20 text-red-400 border border-red-500/30">
                                                            Cancelada
                                                        </span>
                                                    )}
                                                    {cls.en_curso && !cls.cancelada && (
                                                        <span className="relative flex h-2.5 w-2.5 shrink-0" title="En curso">
                                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                                                        </span>
                                                    )}
                                                    {cls.es_cercana && !cls.cancelada && (
                                                        <span className="relative flex h-2.5 w-2.5 shrink-0" title="Comienza en menos de 5 horas">
                                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                                                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-yellow-500"></span>
                                                        </span>
                                                    )}
                                                </div>
                                                {cls.inicio && (
                                                    <p className="text-sm font-medium text-white mt-0.5">{cls.inicio}</p>
                                                )}
                                                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-white/60 mt-1">
                                                    {cls.lugares_disponibles !== null && !cls.cancelada && (
                                                        <span className="flex items-center gap-1">
                                                            <Users className="h-3 w-3" />
                                                            {cls.lugares_disponibles} lugar{cls.lugares_disponibles !== 1 ? 'es' : ''}
                                                        </span>
                                                    )}
                                                </div>
                                                {cls.cancelada && cls.mensaje_cancelacion && (
                                                    <div className="flex items-start gap-1.5 mt-1.5 text-xs text-red-300">
                                                        <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                                                        <span>{cls.mensaje_cancelacion}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="shrink-0 ml-3">
                                                {cls.cancelada ? (
                                                    <Badge variant="outline" className="text-red-400 border-red-500/30 flex items-center gap-1">
                                                        <Ban className="h-3 w-3" /> Cancelada
                                                    </Badge>
                                                ) : cls.inscripto ? (
                                                    <div className="flex items-center gap-2">
                                                        <Badge variant="outline" className="flex items-center gap-1">
                                                            <CheckCircle2 className="h-3 w-3" /> Inscripto
                                                        </Badge>
                                                        {cls.puede_desinscribirse && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="text-white border-white/30 hover:bg-white/10"
                                                                disabled={unregisteringId === cls.id}
                                                                onClick={() => handleUnregister(cls.id)}
                                                            >
                                                                {unregisteringId === cls.id ? 'Saliendo...' : 'Salir'}
                                                            </Button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        disabled={registeringId === cls.id || (cls.lugares_disponibles !== null && cls.lugares_disponibles <= 0)}
                                                        onClick={() => handleRegister(cls.id)}
                                                    >
                                                        {registeringId === cls.id ? 'Inscribiendo...' : 'Inscribirme'}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {!classesLoading && classes.length > 10 && (
                                <Link to="/mi-cuenta/clases" className="block mt-4">
                                    <Button variant="outline" className="w-full uppercase tracking-widest text-xs text-white border-white/30 hover:bg-white/10">
                                        Ver más
                                    </Button>
                                </Link>
                            )}
                        </CardContent>
                    </Card>
                    </>
                )}
            </div>
        </div>
    );
}
